import { AxiosInstance } from "axios";
import { browserId, BROWSER_ID_HEADER } from "./browser-id";

export const basePath = "/flows";
export const pageviewPaths = {
  record: (flowId: string) => basePath + `/${flowId}/pageviews`,
};

// The browser id travels in the X-Cotribute-Browser-Id header, not the body.
export interface PageviewBody {
  stepSlug: string;
  stepType?: string;
  stepIndex?: number;
  isLanding: boolean;
  skinCode?: string;
  brandVariantId?: string;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
  referrer?: string;
}

/**
 * Pull utm_* keys out of the router query; values may be arrays under Next.
 * Keys are matched case-insensitively, as step-router does for the
 * application's utmParams, so the landing row and the application agree.
 */
export const utmFromQuery = (
  query: Record<string, string | string[] | undefined>
): PageviewBody["utm"] | undefined => {
  const lower = new Map(
    Object.entries(query).map(([k, v]) => [k.toLowerCase(), v])
  );
  const pick = (key: string) => {
    const raw = lower.get(key);
    const value = Array.isArray(raw) ? raw[0] : raw;
    return value ? String(value) : undefined;
  };
  const utm = {
    source: pick("utm_source"),
    medium: pick("utm_medium"),
    campaign: pick("utm_campaign"),
    term: pick("utm_term"),
    content: pick("utm_content"),
  };
  return Object.values(utm).some(Boolean) ? utm : undefined;
};

/** `document.referrer` reduced to its hostname; undefined when empty or unparseable. */
export const referrerHostname = (): string | undefined => {
  try {
    return new URL(document.referrer).hostname || undefined;
  } catch {
    return undefined;
  }
};

class PageviewApi {
  api: AxiosInstance;

  constructor(api: AxiosInstance) {
    this.api = api;
  }

  // Fire and forget; a failed analytics write must never reach the applicant.
  record = async (flowId: string, body: PageviewBody): Promise<void> => {
    try {
      await this.api.post(pageviewPaths.record(flowId), body, {
        headers: { [BROWSER_ID_HEADER]: await browserId() },
      });
    } catch {
      // swallowed, see above
    }
  };
}

export default PageviewApi;
